import { IPortfolioDefinitionType, type IPortfolioDefDashboardInputQuery } from 'generated/graphql';
import type { ColDef, ICellRendererParams, IRowNode, ValueGetterParams } from 'ag-grid-community';
import { formatEnum } from 'components/formatter.utils';
import { useMemo, type Dispatch, type ReactElement, type SetStateAction } from 'react';
import { dateReadableValueGetter, getRowDataForRowGroupColumn } from 'components/technical/grids/agGrid.utils';
import GAgGrid from 'components/technical/grids/GAgGrid';
import { Box, Menu, MenuButton } from '@mui/joy';
import PortfolioDefinitionActions from './PortfolioDefinitionActions';
import type { SubfundAutocompleteOptionInput } from 'components/portfolio/fund/SubFundService';
import type { CreateAccountIdAutocompleteOptionsInputAccount } from 'components/portfolio/account/AccountService';
import DialogDropdown from 'components/technical/DialogDropdown/DialogDropdown';
import { createNewPortfolioActions } from './PortfolioDefCards.utils.tsx';
import { useNavigate } from 'react-router';
import { Add } from '@mui/icons-material';
import PortfolioTagsChips from './PortfolioTagsChips';

import { calculatePortfolioFlags } from './PortfolioCard';

export type PortfolioDefRow = IPortfolioDefDashboardInputQuery['portfolio']['definitions'][number];

const defaultColDef = {
  resizable: true,
  sortable: true,
  filter: true,
};

const cellRendererFactory = (
  accounts: CreateAccountIdAutocompleteOptionsInputAccount[],
  subFunds: SubfundAutocompleteOptionInput[],
  allTags: string[]
): ((params: ICellRendererParams<PortfolioDefRow>) => ReactElement | undefined) => {
  return (params: ICellRendererParams<PortfolioDefRow>): ReactElement | undefined => {
    const rowData = getRowDataForRowGroupColumn(params);
    if (!rowData) {
      return undefined;
    }

    if (rowData.genie) {
      return undefined;
    }

    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
        <PortfolioDefinitionActions def={rowData} accounts={accounts} subFunds={subFunds} allTags={allTags} />
      </div>
    );
  };
};

export const PortfolioDefGrid = ({
  defs,
  selectedIds,
  setSelectedIds,
  accounts,
  subFunds,
  allTags,
}: {
  defs: PortfolioDefRow[];
  selectedIds: string[];
  setSelectedIds: Dispatch<SetStateAction<string[]>>;
  accounts: CreateAccountIdAutocompleteOptionsInputAccount[];
  subFunds: SubfundAutocompleteOptionInput[];
  allTags: string[];
}): ReactElement => {
  const navigate = useNavigate();

  const realRebalancedDefs = defs.filter(
    (def) => def.type === IPortfolioDefinitionType.Real || def.type === IPortfolioDefinitionType.Rebalanced
  );

  // use memo to retain cell component instance for dropdown
  const ActionsCellRenderer = useMemo(
    () => cellRendererFactory(accounts, subFunds, allTags),
    [accounts, subFunds, allTags]
  );

  const columns: ColDef<PortfolioDefRow>[] = [
    {
      headerName: 'Name',
      field: 'name',
      type: ['textColumn'],
    },
    {
      headerName: 'Description',
      field: 'description',
      type: ['textColumn'],
      wrapText: true,
      autoHeight: true,
    },
    {
      headerName: 'Type',
      field: 'type',
      type: ['textColumn'],
      width: 120,
      valueGetter: (params: ValueGetterParams<PortfolioDefRow>): string | undefined => {
        return params.data ? formatEnum(params.data.type) : undefined;
      },
    },
    {
      headerName: 'Subtype',
      field: 'subType',
      type: ['textColumn'],
      width: 150,
      valueGetter: (params: ValueGetterParams<PortfolioDefRow>): string | undefined => {
        return params.data ? formatEnum(params.data.subType) : undefined;
      },
    },
    {
      colId: 'createdAt',
      headerName: 'Created at',
      type: 'dateColumn',
      valueGetter: (params: ValueGetterParams<PortfolioDefRow>): string | undefined => {
        return params.data ? dateReadableValueGetter(params.data.createdAt) : undefined;
      },
    },
    {
      headerName: 'Tags',
      field: 'tags',
      width: 200,
      cellRenderer: (params: ICellRendererParams<PortfolioDefRow>): ReactElement | null => {
        if (!params.data?.tags || params.data.tags.length === 0) {
          return null;
        }
        return <PortfolioTagsChips tags={params.data.tags} />;
      },
      valueGetter: (params: ValueGetterParams<PortfolioDefRow>): string[] => {
        return params.data?.tags ?? [];
      },
    },
    {
      headerName: 'Transaction Tag',
      field: 'rebalancedDetails.transactionTag',
      type: ['textColumn'],
      width: 150,
    },
    {
      headerName: 'Actions',
      colId: 'actions',
      sortable: false,
      filter: false,
      width: 100,
      cellRenderer: ActionsCellRenderer,
      suppressHeaderMenuButton: true,
    },
  ];

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <DialogDropdown>
          <MenuButton variant="solid" color="primary" startDecorator={<Add />}>
            Create new portfolio
          </MenuButton>
          <Menu
            modifiers={[
              {
                name: 'offset',
              },
            ]}
          >
            {createNewPortfolioActions({ navigate, accounts, subFunds })}
          </Menu>
        </DialogDropdown>
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        <GAgGrid<PortfolioDefRow>
          rowData={realRebalancedDefs}
          height={'100%'}
          minHeight={'500px'}
          columnDefs={columns}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data.id}
          suppressCellFocus={true}
          onFirstDataRendered={(params) => {
            const selectedNodes: IRowNode<PortfolioDefRow>[] = [];
            params.api.forEachNode((node) => {
              if (selectedIds.includes(node.id ?? '')) {
                selectedNodes.push(node);
              }
            });
            params.api.setNodesSelected({ nodes: selectedNodes, newValue: true });
          }}
          rowSelection={{
            mode: 'multiRow',
            enableClickSelection: true,
            hideDisabledCheckboxes: true,
            isRowSelectable: (params: IRowNode<PortfolioDefRow>) => {
              const rowData = params.data;
              if (!rowData) {
                return false;
              }
              return !calculatePortfolioFlags(rowData).showNoRulesWarning;
            },
          }}
          onSelectionChanged={(event) => {
            setSelectedIds(event.api.getSelectedRows().map((row) => row.id));
          }}
        />
      </Box>
    </>
  );
};
